import React, {
  Fragment, useEffect, useRef, useState
} from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Callout, Intent } from '@blueprintjs/core';
import { handleLogout as authenticationHandleLogout } from 'data/authentication/authentication.actions';
import i18n from 'i18n';
import { generateLogiToken } from 'utils/auth';
import * as authenticationActions from 'data/authentication/authentication.actions';
import IFrame from '../iframe';
import XMS from './xms';
import { findExternalAppConfig } from './utils';

export interface ExternalAppsProps {
  location: {
    pathname: string,
    hash: string
  }
  isExternalDashboardEnabled: boolean,
  isSecurityGatewayEnabled: boolean,
  isXmsMenusEnabled: boolean,
  isNetworkTopologyEnabled: boolean,
  isAlarmFeatureEnabled: boolean,
  isAlarmNotificationProfileEnabled: boolean,
  externalDashboardIP: string,
  logiTokenUpdated: boolean,
  handleLogout: CallableFunction,
  externalDashboardId: string,
  systemHealthDashboardId: string,
  aggLicensesDashboardId: string,
  xcipioLicensesDashboardId: string,
  userId: string,
  logiOAuthClientId: string,
  logiOAuthClientName: string,
  logiOAuthClientSecret: string,
  logiSupervisorUserName: string,
  logiSupervisorPswd: string,
  logiRefreshTimeInSec: number,
}

export const ExternalApps = ({
  location,
  isExternalDashboardEnabled,
  isSecurityGatewayEnabled,
  isXmsMenusEnabled,
  isNetworkTopologyEnabled,
  isAlarmFeatureEnabled,
  isAlarmNotificationProfileEnabled,
  externalDashboardIP,
  logiTokenUpdated,
  handleLogout,
  externalDashboardId,
  systemHealthDashboardId,
  aggLicensesDashboardId,
  xcipioLicensesDashboardId,
  userId: user,
  logiOAuthClientId,
  logiOAuthClientName,
  logiOAuthClientSecret,
  logiSupervisorUserName,
  logiSupervisorPswd,
  logiRefreshTimeInSec
}: ExternalAppsProps) => {
  const [tab, setTab] = useState({ route: '', url: '' });
  const [logiAuthToken, setLogiAuthToken] = useState('');
  const timeouts = useRef<any>([]);

  useEffect(() => {
    const externalAppConfig = findExternalAppConfig(location.pathname, isExternalDashboardEnabled,
      isSecurityGatewayEnabled, isXmsMenusEnabled, isNetworkTopologyEnabled,
      isAlarmFeatureEnabled, isAlarmNotificationProfileEnabled,
      externalDashboardId,
      systemHealthDashboardId,
      aggLicensesDashboardId,
      xcipioLicensesDashboardId,);

    if (externalAppConfig == null) {
      return;
    }

    const { route, url = '' } = externalAppConfig;

    setTab({ route, url });

    authenticationActions.checkLogiToken({
      user,
      externalDashboardIP,
      logiOAuthClientId,
      logiOAuthClientName,
      logiOAuthClientSecret,
      logiSupervisorUserName,
      logiSupervisorPswd,
    });

    const logiToken = localStorage.getItem('logi_auth_token') as string;

    if (isExternalDashboardEnabled && logiToken) {
      setLogiAuthToken(logiToken);
      if (location.pathname.includes('external/dashboard')) {
        addDashboard(logiToken, externalDashboardId);
      } else if (location.pathname.includes('external/systemhealth')) {
        addDashboard(logiToken, systemHealthDashboardId);
      } else if (location.pathname.includes('/external/monitoring/agglicenses')) {
        addDashboard(logiToken, aggLicensesDashboardId);
      } else if (location.pathname.includes('/external/monitoring/xcipiolicenses')) {
        addDashboard(logiToken, xcipioLicensesDashboardId);
      }
    }
    return () => {
      timeouts.current.forEach(clearInterval);
    }
  }, [location.pathname, logiTokenUpdated]);

  async function createOrGetEmbedManager(token: string) {
    return (window as any).initComposerEmbedManager({
      initialToken: token
    });
  }

  async function addDashboard(token: string, id: string) { // id of the composer dashboard
    const embedManager = await createOrGetEmbedManager(token);

    const dashboard = await embedManager.createComponent('dashboard', {
      dashboardId: id,
    });

    dashboard.render(document.querySelector('#embeddedLogi'), { paddingTop: '50px !important', marginTop: '50px !important', height: 'calc(100% - 103px);' });
    dashboard.addEventListener('composer-dashboard-loaded', (e: any) => {
      const data = e.detail.dashboard;
      const { id, componentInstanceId, name } = data;
      if ((location.pathname.includes('external/dashboard') && id === externalDashboardId)
        || (location.pathname.includes('external/systemhealth') && id === systemHealthDashboardId)
        || (location.pathname.includes('external/monitoring/agglicenses') && id === aggLicensesDashboardId)
        || (location.pathname.includes('external/monitoring/xcipiolicenses') && id === xcipioLicensesDashboardId)) {
        timeouts.current.push(setInterval(() => {
          // embedManager.refreshComponent(componentInstanceId);
          dashboard.refreshData();
        }, (logiRefreshTimeInSec * 1000)));
      } else {
        embedManager.removeComponent(componentInstanceId);
      }
    });
    return dashboard;
  }

  async function refreshDashboard(newToken: string) {
    const embedManager = await createOrGetEmbedManager(newToken);
    embedManager.updateToken(newToken).then(() => {
      embedManager.refresh();
    });
  }

  (function application() {
    document.addEventListener('composer-unauthorized', async () => {
      const { tokenValue, expiration } = await generateLogiToken({
        user,
        externalDashboardIP,
        logiOAuthClientId,
        logiOAuthClientName,
        logiOAuthClientSecret,
        logiSupervisorUserName,
        logiSupervisorPswd,
      });

      localStorage.setItem('logi_auth_token', tokenValue);
      localStorage.setItem('logi_auth_token_expiration', expiration);
      refreshDashboard(tokenValue);
    })
  }());

  let displayContent = <IFrame url={tab.url} />;

  if (location.pathname.includes('xms')) {
    displayContent = <XMS pathName={tab.url} handleLogout={() => null} />;
  } else if (location.pathname.includes('external/dashboard') || location.pathname.includes('external/systemhealth') || location.pathname.includes('external/monitoring')) {
    displayContent = (
      <Fragment>
        <div id="embeddedLogi" />
      </Fragment>
    );
  }

  return displayContent;
};

const mapStateToProps = ({
  discoveryXcipio: {
    discoveryXcipioSettings: {
      isExternalDashboardEnabled,
      isSecurityGatewayEnabled,
      isXmsMenusEnabled,
      isNetworkTopologyEnabled,
      isAlarmFeatureEnabled,
      isAlarmNotificationProfileEnabled,
      externalDashboardIP,
      externalDashboardId,
      systemHealthDashboardId,
      aggLicensesDashboardId,
      xcipioLicensesDashboardId,
      logiOAuthClientId,
      logiOAuthClientName,
      logiOAuthClientSecret,
      logiSupervisorUserName,
      logiSupervisorPswd,
      logiRefreshTimeInSec
    },
  },
  authentication: {
    logiTokenUpdated,
    userId,
  },
}: any) =>
({
  isExternalDashboardEnabled,
  isSecurityGatewayEnabled,
  isXmsMenusEnabled,
  isNetworkTopologyEnabled,
  isAlarmFeatureEnabled,
  isAlarmNotificationProfileEnabled,
  externalDashboardIP,
  logiTokenUpdated,
  externalDashboardId,
  systemHealthDashboardId,
  aggLicensesDashboardId,
  xcipioLicensesDashboardId,
  userId,
  logiOAuthClientId,
  logiOAuthClientName,
  logiOAuthClientSecret,
  logiSupervisorUserName,
  logiSupervisorPswd,
  logiRefreshTimeInSec
});

const mapDispatchToProps = {
  handleLogout: authenticationHandleLogout,
  doCheckToken: authenticationActions.doCheckToken,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ExternalApps));
